/**
 * Courier Code Mapping between KiriminAja (Ongkir/Pricing) and BinderByte (Tracking/Resi)
 * 
 * IMPORTANT: These two APIs use DIFFERENT courier codes!
 * - KiriminAja: For pricing/ongkir operations
 * - BinderByte: For tracking/resi operations
 */

// Mapping from KiriminAja codes to BinderByte codes
const KIRIMINAJA_TO_BINDERBYTE = {
  // Express couriers
  'jne': 'jne',                    // JNE - same
  'jnt': 'jnt',                    // J&T Express - same
  'tiki': 'tiki',                  // Tiki - same
  'sicepat': 'sicepat',            // Sicepat - same
  'posindonesia': 'pos',           // ⚠️ DIFFERENT: Pos Indonesia
  'paxel': 'paxel',                // Paxel - same
  'ncs': 'ncs',                    // NCS Courier - same
  'anteraja': 'anteraja',          // AnterAja - same
  'sentral': null,                 // ⚠️ NOT SUPPORTED in BinderByte
  'jtcargo': 'jnt',                // ⚠️ DIFFERENT: J&T Cargo -> JNT code
  'lion': 'lion',                  // Lion Parcel - same
  'rpx': 'rpm',                    // ⚠️ DIFFERENT: RPX -> RPM
  'ninja': 'ninja',                // Ninja - same
  'idx': 'idexpress',              // ID Express - same
  'sap': 'sap',                    // SAP Express - same
  'spx': 'spx',                    // SPX - same
  
  // Instant couriers
  'gosend': 'gosend',              // GoSend - same
  'grab_express': 'grabbed',       // ⚠️ DIFFERENT: Grab Express -> grabbed
  'borzo': 'borzo',                // Borzo - same
};

// Mapping from BinderByte codes to KiriminAja codes
const BINDERBYTE_TO_KIRIMINAJA = {
  'jne': 'jne',                    // JNE - same
  'jnt': 'jnt',                    // J&T Express - same
  'tiki': 'tiki',                  // Tiki - same
  'sicepat': 'sicepat',            // Sicepat - same
  'pos': 'posindonesia',           // ⚠️ DIFFERENT: Pos Indonesia
  'paxel': 'paxel',                // Paxel - same
  'ncs': 'ncs',                    // NCS Courier - same
  'anteraja': 'anteraja',          // AnterAja - same
  'lion': 'lion',                  // Lion Parcel - same
  'rpm': 'rpx',                    // ⚠️ DIFFERENT: RPM -> RPX
  'ninja': 'ninja',                // Ninja - same
  'idexpress': 'idx',              // ID Express - same
  'sap': 'sap',                    // SAP Express - same
  'spx': 'spx',                    // SPX - same
  'grabbed': 'grab_express',       // ⚠️ DIFFERENT: grabbed -> Grab Express
  'borzo': 'borzo',                // Borzo - same
  'wahana': null,                  // ⚠️ NOT IN KiriminAja
  'dse': null,                     // ⚠️ NOT IN KiriminAja
  'first': null,                   // ⚠️ NOT IN KiriminAja
};

// Public courier slugs that must be sent to the tracking backend as another code.
const TRACKING_PUBLIC_ALIASES = {
  "j&t": "jnt",
  "j&t-cargo": "jnt_cargo",
};

// Public courier slugs that must be sent to the tracking backend as another code.
const TRACKING_BACKEND_ALIASES = {
  jet: "jnt",
  jxe: "jnt",
};

/**
 * Convert KiriminAja courier code to BinderByte courier code
 * Used when tracking a shipment that was priced with KiriminAja
 * 
 * @param {string} kiriminajaCode - Courier code from KiriminAja (e.g., 'jne', 'posindonesia')
 * @returns {string|null} - BinderByte courier code, or null if not supported
 * 
 * @example
 * const trackingCode = convertToBinderbyte('jne');        // returns 'jne'
 * const trackingCode = convertToBinderbyte('posindonesia'); // returns 'pos'
 * const trackingCode = convertToBinderbyte('grab_express');  // returns 'grabbed'
 */
export function convertToBinderbyte(kiriminajaCode) {
  const code = kiriminajaCode?.toLowerCase().trim();
  
  if (!code) {
    console.warn('[Courier Mapping] Empty or invalid KiriminAja code');
    return null;
  }

  if (!(code in KIRIMINAJA_TO_BINDERBYTE)) {
    console.warn(`[Courier Mapping] Unknown KiriminAja code: ${code}`);
    return null;
  }

  const binderbytecode = KIRIMINAJA_TO_BINDERBYTE[code];
  
  if (binderbytecode === null) {
    console.warn(`[Courier Mapping] KiriminAja courier "${code}" is not supported in BinderByte`);
    return null;
  }

  return binderbytecode;
}

/**
 * Convert BinderByte courier code to KiriminAja courier code
 * Used when getting pricing for tracking information
 * 
 * @param {string} binderbytecode - Courier code from BinderByte (e.g., 'jne', 'pos')
 * @returns {string|null} - KiriminAja courier code, or null if not supported
 * 
 * @example
 * const pricingCode = convertToKiriminaja('jne');        // returns 'jne'
 * const pricingCode = convertToKiriminaja('pos');        // returns 'posindonesia'
 * const pricingCode = convertToKiriminaja('grabbed');    // returns 'grab_express'
 */
export function convertToKiriminaja(binderbytecode) {
  const code = binderbytecode?.toLowerCase().trim();
  
  if (!code) {
    console.warn('[Courier Mapping] Empty or invalid BinderByte code');
    return null;
  }

  if (!(code in BINDERBYTE_TO_KIRIMINAJA)) {
    console.warn(`[Courier Mapping] Unknown BinderByte code: ${code}`);
    return null;
  }

  const kiriminajaCode = BINDERBYTE_TO_KIRIMINAJA[code];
  
  if (kiriminajaCode === null) {
    console.warn(`[Courier Mapping] BinderByte courier "${code}" is not supported in KiriminAja`);
    return null;
  }

  return kiriminajaCode;
}

/**
 * Check if a KiriminAja code is supported in BinderByte
 * 
 * @param {string} kiriminajaCode - Courier code from KiriminAja
 * @returns {boolean} - true if supported in BinderByte, false otherwise
 */
export function isSupportedInBinderbyte(kiriminajaCode) {
  const code = kiriminajaCode?.toLowerCase().trim();
  return code in KIRIMINAJA_TO_BINDERBYTE && KIRIMINAJA_TO_BINDERBYTE[code] !== null;
}

/**
 * Check if a BinderByte code is supported in KiriminAja
 * 
 * @param {string} binderbytecode - Courier code from BinderByte
 * @returns {boolean} - true if supported in KiriminAja, false otherwise
 */
export function isSupportedInKiriminaja(binderbytecode) {
  const code = binderbytecode?.toLowerCase().trim();
  return code in BINDERBYTE_TO_KIRIMINAJA && BINDERBYTE_TO_KIRIMINAJA[code] !== null;
}

/**
 * Get all KiriminAja couriers that are NOT supported in BinderByte
 * Useful for warnings/alerts in UI
 * 
 * @returns {string[]} - Array of unsupported KiriminAja codes
 */
export function getUnsupportedInBinderbyte() {
  return Object.entries(KIRIMINAJA_TO_BINDERBYTE)
    .filter(([_, binderbyteCode]) => binderbyteCode === null)
    .map(([kiriminajaCode]) => kiriminajaCode);
}

/**
 * Get all BinderByte couriers that are NOT supported in KiriminAja
 * 
 * @returns {string[]} - Array of unsupported BinderByte codes
 */
export function getUnsupportedInKiriminaja() {
  return Object.entries(BINDERBYTE_TO_KIRIMINAJA)
    .filter(([_, kiriminajaCode]) => kiriminajaCode === null)
    .map(([binderbytecode]) => binderbytecode);
}

/**
 * Get all courier codes that differ between KiriminAja and BinderByte
 * 
 * @returns {Object[]} - Array of mapping objects with different codes
 */
export function getDifferentCodes() {
  return Object.entries(KIRIMINAJA_TO_BINDERBYTE)
    .filter(([kiriminajaCode, binderbyteCode]) => 
      binderbyteCode !== null && kiriminajaCode !== binderbyteCode
    )
    .map(([kiriminajaCode, binderbyteCode]) => ({
      kiriminaja: kiriminajaCode,
      binderbyte: binderbyteCode,
    }));
}

/**
 * Format courier name with warning if codes differ
 * 
 * @param {string} courierName - Display name of courier
 * @param {string} kiriminajaCode - KiriminAja code
 * @returns {string} - Formatted name with warning indicator if needed
 */
export function formatCourierName(courierName, kiriminajaCode) {
  const binderbyteCode = convertToBinderbyte(kiriminajaCode);
  
  if (!binderbyteCode) {
    return `${courierName} ⚠️ (Tidak support untuk tracking)`;
  }
  
  if (kiriminajaCode !== binderbyteCode) {
    return `${courierName} (⚠️ code berbeda untuk tracking)`;
  }
  
  return courierName;
}

export function mapTrackingCourierToBackend(courierCode) {
  const code = normalizeTrackingCourierSlug(courierCode);

  if (!code) {
    return code ?? "";
  }

  return TRACKING_BACKEND_ALIASES[code] ?? code;
}

export function normalizeTrackingCourierSlug(courierCode) {
  let code = courierCode?.toLowerCase().trim();

  if (!code) {
    return code ?? "";
  }

  try {
    while (code.includes("%")) {
      const decoded = decodeURIComponent(code);
      if (decoded === code) break;
      code = decoded.toLowerCase().trim();
    }
  } catch (e) {
    // Ignore URIError on malformed percent encoding
  }

  return TRACKING_PUBLIC_ALIASES[code] ?? code;
}

export default {
  KIRIMINAJA_TO_BINDERBYTE,
  BINDERBYTE_TO_KIRIMINAJA,
  TRACKING_PUBLIC_ALIASES,
  TRACKING_BACKEND_ALIASES,
  convertToBinderbyte,
  convertToKiriminaja,
  isSupportedInBinderbyte,
  isSupportedInKiriminaja,
  getUnsupportedInBinderbyte,
  getUnsupportedInKiriminaja,
  getDifferentCodes,
  formatCourierName,
  normalizeTrackingCourierSlug,
  mapTrackingCourierToBackend,
};
